import { computed, ref } from "vue";
import { useChannelPartner } from "@/shared/account/composables/useChannelPartner";
import { useUserAddresses } from "@/shared/account/composables/useUserAddresses";
import { SPECIAL_CASES_EUR } from "../constants/language-and-currency";
import { globals } from "../globals";
import { useCurrency } from "./useCurrency";
import { useGeolocation } from "./useGeolocation";

export { SPECIAL_CASES_EUR };
type DataAreaId = "FRA" | "MUC" | "SWE" | "UK0" | "JPN" | "PRC" | "THO" | "BRA" | "IND";

const { countryCodeISO3 } = useGeolocation();
const { currentCurrency } = useCurrency();
const { isChannelPartner, destinationDataAreaId, operationalCurrency } = useChannelPartner();
const { defaultShippingAddressCountryCode, defaultBillingAddressCountryCode } = useUserAddresses();
const userCountryCode = ref<string>("");
const userBillingCountryCode = ref<string>("");
// Store selected payment method to avoid circular dependency
const selectedPaymentMethodForDataAreaId = ref<string | undefined>(undefined);

// Currency of the fulfillment region (content + warehouse). Equals the display currency
// except for channel partners, whose region differs from what they're priced in.
const regionCurrency = computed(() =>
  isChannelPartner.value && operationalCurrency.value ? operationalCurrency.value : currentCurrency.value.code,
);

const dataAreaId = computed(() => {
  const countryCodeToComputeDataAreaId =
    userCountryCode.value || defaultShippingAddressCountryCode.value || countryCodeISO3.value || "";
  const selectedPaymentMethod = selectedPaymentMethodForDataAreaId.value;
  const result = getDataAreaId(regionCurrency.value, countryCodeToComputeDataAreaId, selectedPaymentMethod);

  localStorage.setItem("dataAreaId", result);

  return result;
});

const billingDataAreaId = computed(() => {
  const countryCodeToComputeDataAreaId =
    userBillingCountryCode.value || defaultBillingAddressCountryCode.value || countryCodeISO3.value || "";
  const selectedPaymentMethod = selectedPaymentMethodForDataAreaId.value;
  const result = getDataAreaId(regionCurrency.value, countryCodeToComputeDataAreaId, selectedPaymentMethod);

  localStorage.setItem("billingDataAreaId", result);

  return result;
});

function setCountryCode(countryCode: string) {
  userCountryCode.value = countryCode;
}

function setBillingCountryCode(countryCode: string) {
  userBillingCountryCode.value = countryCode;
}

function setSelectedPaymentMethodForDataAreaId(paymentMethod: string | undefined) {
  selectedPaymentMethodForDataAreaId.value = paymentMethod;
}

function getDataAreaId(currency: string, countryCode: string, selectedPaymentMethod?: string): DataAreaId {
  // Channel partners route to their configured destination data area regardless of country/currency.
  if (isChannelPartner.value && destinationDataAreaId.value) {
    return destinationDataAreaId.value as DataAreaId;
  }

  if (currency === "EUR") {
    if (SPECIAL_CASES_EUR.FRA.includes(countryCode)) {
      return "FRA";
    }
    // Special case: If sweAsMUCInEURCybersource is enabled, payment method is CreditCard,
    // and country is in the SWE list, return "MUC" instead of "SWE"
    const locationComponents = location.pathname.split("/");
    const isCheckoutPage =
      locationComponents.find((item) => item === "checkout" || item === "quote") !== undefined &&
      !locationComponents.find((item) => item === "account");
    const countryIsOnSweList = SPECIAL_CASES_EUR.SWE.includes(countryCode);
    const paymentMethodIsCreditCard =
      (selectedPaymentMethod ?? selectedPaymentMethodForDataAreaId.value) === "CreditCard";
    const isSweInMucSpecialCase =
      globals.sweAsMUCInEURCybersource && countryIsOnSweList && (isCheckoutPage ? paymentMethodIsCreditCard : true);
    if (isSweInMucSpecialCase) {
      return "MUC";
    }
    if (SPECIAL_CASES_EUR.SWE.includes(countryCode)) {
      return "SWE";
    }

    return "MUC"; // Default for EUR when no special cases match
  }

  // Fallback or default mapping for other currencies
  switch (currency) {
    case "GBP":
      return "UK0";
    case "JPY":
      return "JPN";
    case "CNY":
      return "PRC";
    case "USD":
      return "THO";
    case "BRL":
      return "BRA";
    case "SEK":
      return "SWE";
    case "INR":
      return "IND";
    default:
      throw new Error(`Unsupported currency: ${currency}`);
  }
}

export function useDataAreaId() {
  return {
    setCountryCode,
    dataAreaId,
    getDataAreaId,
    billingDataAreaId,
    setBillingCountryCode,
    userBillingCountryCode,
    setSelectedPaymentMethodForDataAreaId,
    regionCurrency,
  };
}
